import type { ResolvedDocumentGroup } from '~/composables/useDocumentGroups'

/**
 * The sidebar's display filter over the derived document groups (MAR-125; split out of
 * `FileExplorer.vue` in MAR-948).
 *
 * Project mode nests matters as `subgroups` (AlexAmin/marcus#83), and the query filters both levels
 * the same way: a matter survives if any of ITS documents match, and a client survives if either its
 * own documents or any of its (already-filtered) matters still have something in them — otherwise a
 * client whose only match lives under a case would drop out of the list entirely, which is exactly
 * the "nothing may vanish" failure mode of the case-only flattening this replaced.
 *
 * Empty groups stay when there is no query: a case/client with no documents makes this view double
 * as a navigable list of cases/clients. A group is dropped only when a query is active and nothing
 * in it (or its matters) matches.
 */
export function filterDocumentGroups(groups: readonly ResolvedDocumentGroup[], query: string): ResolvedDocumentGroup[] {
  const q = query.trim().toLowerCase()
  const matches = <T extends { name: string }>(docs: T[]) => (q ? docs.filter(d => d.name.toLowerCase().includes(q)) : docs)
  const out: ResolvedDocumentGroup[] = []
  for (const group of groups) {
    const documents = matches(group.documents)
    const subgroups = group.subgroups
      ?.map(s => ({ ...s, documents: matches(s.documents) }))
      .filter(s => !q || s.documents.length > 0)
    if (q && documents.length === 0 && (subgroups?.length ?? 0) === 0) continue
    out.push({ ...group, documents, subgroups })
  }
  return out
}
